import cityService from '../services/cityService';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import countryService from '../services/countryService';

const citiesCollectionName = 'cities';
export const useCity = () => {
  return useQuery({
    queryKey: [citiesCollectionName],
    queryFn: () => cityService.getAll(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

export const useAddCity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cityService.add,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [citiesCollectionName] });
    },
  });
};

export const useEditCity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cityService.edit,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [citiesCollectionName] });
    },
  });
};

export const useDeleteCity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cityService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [citiesCollectionName] });
    },
  });
};

export const useCityNameById = (cityId) => {
  return useQuery({
    queryKey: [citiesCollectionName, cityId],
    queryFn: () => cityService.getNameById(cityId),
    enabled: !!cityId, // only run when cityId is truthy
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const useCityIdByName = (cityName) => {
  return useQuery({
    queryKey: [citiesCollectionName, cityName],
    queryFn: () => cityService.getIdByName(cityName),
    enabled: !!cityName, // only run when cityName is truthy
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

export const useCountryCities = (countryId) => {
  return useQuery({
    queryKey: [citiesCollectionName, countryId],
    queryFn: () => cityService.getCountryCities(countryId),
    enabled: !!countryId,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

export const useCitiesByCountryName = (countryName) => {
  return useQuery({
    queryKey: [citiesCollectionName, 'byCountryName', countryName],
    queryFn: async () => {
      // First get the country ID by name
      const country = await countryService.getByName(countryName);
      if (!country) return [];

      // Then get cities for that country
      return cityService.getCountryCities(country.id);
    },
    enabled: !!countryName,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

